<template>
  <div ref="container" class="timestamp-annotation" />
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  ANNOTATION_MODE,
  ANNOTATION_TYPE,
  HIGHLIGHTER_SELECTOR_TYPE,
} from "@modules/annotation/service/annotation.constant";
import { isDefined, isEmpty, isNull } from "@utils/var.check";
import { AnnotationEventBus } from "@modules/annotation/service/annotation.event.bus";
import DOMUtils from "@utils/dom.utils";
import { useAnnotation } from "@modules/annotation/composables/annotation.ts";
import { useAnnotationNote } from "@modules/annotation/composables/annotation.note.ts";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { useAnnotationAnchor } from "@modules/annotation/composables/annotation.anchor.ts";
import {
  buildMediaObjectAnchor,
  parseOwningComponentIdFromAnchor,
} from "@modules/annotation/service/annotation.anchor.utils.ts";
import { usePageDrawer } from "@modules/app/composables/page.drawer.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import {
  Annotation,
  AnnotationContext,
  HighlightedData,
  HighlighterSelectorModel,
} from "@modules/annotation/types.ts";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";

interface Props {
  anchor?: string;
  annotationResourceType?: string;
  index?: string;
  annotationResourceId?: string;
  ownerType?: string;
  question?: unknown[];
  getMentionableTeacherList?: () => Record<string, unknown>;
  getMentionableStudentList?: () => Record<string, unknown>;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_parentComponent?: object;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_isDynamicallyMounted?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  anchor: "",
  annotationResourceType: "",
  index: "",
  annotationResourceId: "",
  ownerType: "USER",
  question: () => [],
  getMentionableTeacherList: () => () => ({}),
  getMentionableStudentList: () => () => ({}),
  dynamicMountChildMixin_parentComponent: () => ({}),
  dynamicMountChildMixin_isDynamicallyMounted: false,
});

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const annotationContext = inject<AnnotationContext>("annotationContext");
const store = useStore();

const currentAnchor = computed(() => buildMediaObjectAnchor(props.anchor, props.index));

const { isStudentView, annotations, getWithNote, setWithNote, createDraftAnnotation } =
  useAnnotation(currentAnchor.value);

const { handleAnchor } = useAnnotationAnchor();
const attemptAdapter = useAnnotationAttemptAdapter();

const getMediaUuid = () => {
  const mediaElement = container.value?.closest(".media");
  return mediaElement instanceof HTMLElement ? (mediaElement.dataset.uuid ?? "") : "";
};

const { selectedAnnotation, handleEditNotes } = useAnnotationNote({
  deactiveNoteToggle: undefined,
  handleNoteChangeConfirm: undefined,
  handleNoteChangeCancel: undefined,
  getContext: undefined,
  getMentionableTeacherList: props.getMentionableTeacherList,
  getMentionableStudentList: props.getMentionableStudentList,
  mode: "",
});

const eventBusWatch = useEventBusWatch();

const container = ref<HTMLDivElement>();
const mode = ref(ANNOTATION_MODE.VIEW);
const isTranscriptEnabled = ref(false);
const { handleCollapseDrawer } = usePageDrawer({
  drawerName: annotationContext?.drawerName ?? "",
});
const unwatch = ref<(() => void) | null>(null);

const isView = computed(() => mode.value === ANNOTATION_MODE.VIEW);
const isEdit = computed(() => mode.value === ANNOTATION_MODE.EDIT);
const hasSelectedTimestampAnnotation = computed(() => {
  return (
    selectedAnnotation.value.annotation &&
    selectedAnnotation.value.annotation.type === ANNOTATION_TYPE.TIMESTAMP_ANNOTATION &&
    selectedAnnotation.value.annotation.anchor === currentAnchor.value
  );
});

const highlighterClass = computed(() => (isStudentView.value ? "highlight-2" : "assess-3"));

watch(mode, () => {
  if (isView.value) {
    // Render updated annotations
    renderAnnotation();
  } else if (isEdit.value) {
    if (hasSelectedTimestampAnnotation.value) {
      renderAnnotation();
      if (getWithNote.value) {
        nextTick(() => {
          handleCollapseDrawer().then(() => {
            handleEditNotes(ANNOTATION_TYPE.TIMESTAMP_ANNOTATION, props.question, {
              isTranscriptEnabled: isTranscriptEnabled.value,
            });
          });
        });
      }
    }
  }
});

const handleTrigger = (params: Record<string, unknown>) => {
  if (!annotationContext) return;

  isTranscriptEnabled.value = params.isTranscriptEnabled === true;
  setWithNote(true);
  const highlightData = populateHighlightData(params);
  const position = getMediaPosition();
  const hasAnnotationResourceId =
    isDefined(props.annotationResourceId) && !isEmpty(props.annotationResourceId);
  const ownerId = annotationContext?.userId ?? attemptAdapter.currentUserId.value;
  const contextComponentId = Number(annotationContext?.componentId);
  const owningComponentId =
    Number.isFinite(contextComponentId) && contextComponentId > 0
      ? contextComponentId
      : parseOwningComponentIdFromAnchor(props.anchor);

  const requestData = {
    resourceUuid: annotationContext.resourceUuid,
    resourceType: annotationContext.resourceType,
    annotationResourceId: hasAnnotationResourceId ? props.annotationResourceId : owningComponentId,
    annotationResourceType: props.annotationResourceType,
    anchor: buildMediaObjectAnchor(props.anchor, props.index),
    contextType: annotationContext?.contextType,
    type: ANNOTATION_TYPE.TIMESTAMP_ANNOTATION,
    data: highlightData,
    owner: annotationContext.userId,
    collectionOwner: annotationContext.userId,
    groupingId: annotationContext.pageId,
    allowComment: true,
    ownerType: props.ownerType,
    ownerId,
    title: params.mediaTitle,
    indexPosition: position,
    visibleByStudents: false,
    mediaUuid: getMediaUuid(),
  };

  createDraftAnnotation(requestData);
};

const populateHighlightData = (data: Record<string, unknown>) => {
  const id = Date.now();
  return {
    highlighterId: id,
    highlighterClass: highlighterClass.value,
    highlightedContent: isNull(data.highlightedContent) ? "" : data.highlightedContent,
    highlighterSelectors: [
      {
        type: HIGHLIGHTER_SELECTOR_TYPE.FRAGMENT_SELECTOR,
        start: data.timestamp,
      },
    ],
  };
};

const getMediaPosition = () => {
  const richText = container.value?.closest(".annotatable-richtext");
  const selector = `.media [index='${props.index}']`;
  let position;

  if (richText) {
    // Get position of media player in richtext
    DOMUtils.elOf(richText, selector).forEach((element: Element) => {
      const range = document.createRange();
      range.selectNodeContents(element);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(richText);
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      position = preCaretRange.toString().length;
    });
  }
  return position;
};

const renderAnnotation = () => {
  const annotationList: object[] = [];
  annotations.value.forEach((annotationItem: Annotation) => {
    if (
      annotationItem.type !== ANNOTATION_TYPE.TIMESTAMP_ANNOTATION ||
      annotationItem.anchor !== currentAnchor.value
    ) {
      return;
    }

    const data = annotationItem.data as HighlightedData;
    const highlighterSelectors = data.highlighterSelectors as HighlighterSelectorModel[];

    annotationList.push({
      timestamp: highlighterSelectors[0].start,
      highlighterId: data.highlighterId,
      uuid: annotationItem.uuid,
      highlighterClass: data.highlighterClass,
    });
  });

  AnnotationEventBus.$emit("renderTimestampAnnotation", {
    annotationList: annotationList,
    anchor: currentAnchor.value,
  });
};

// Expose methods for external use
defineExpose({
  handleTrigger,
  renderAnnotation,
  handleAnchor,
});

onMounted(() => {
  renderAnnotation();

  unwatch.value = store.watch(
    () => store.getters["annotation/getSelectedAnnotation"],
    () => {
      if (selectedAnnotation.value !== undefined) {
        if (selectedAnnotation.value.mode === ANNOTATION_MODE.EDIT) {
          mode.value = ANNOTATION_MODE.EDIT;
        } else if (selectedAnnotation.value.mode === ANNOTATION_MODE.DELETE) {
          mode.value = ANNOTATION_MODE.DELETE;
        } else if (selectedAnnotation.value.mode === ANNOTATION_MODE.VIEW) {
          mode.value = ANNOTATION_MODE.VIEW;
        }
      }
    },
  );

  eventBusWatch.trackWatch(
    AnnotationEventBus.$on("anchorTimestampAnnotation", (highlighterId: string) => {
      handleAnchor(highlighterId);
    }),
  );
});

onUnmounted(() => {
  if (unwatch.value) {
    unwatch.value();
  }
});
</script>
